/* hs-eslint ignored failing-rules */

/* eslint-disable react-hooks/exhaustive-deps */
'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import Subtext from 'pricing-pages-unified-ui/components/common/text/Subtext';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { SALES } from 'self-service-api/constants/ProductLines';
import styled from 'styled-components';
import H5 from 'UIComponents/elements/headings/H5';
import Small from 'UIComponents/elements/Small';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIStepperInput from 'UIComponents/input/UIStepperInput';
import UIFlex from 'UIComponents/layout/UIFlex';
import UILink from 'UIComponents/link/UILink';
import { PAID_VS_FREE_USERS_MODAL } from '../../constants/Tracking';
import { activeCurrencySelector } from '../../selectors/CurrencySelectors';
import { isMon606InAppRunningForPublicUserSelector, isMon606VariantSelector } from '../../selectors/ExperimentTreatmentSelectors';
import { calculatedPriceForProductConfigurationsInListSelector } from '../../selectors/PricingSelectors';
import { hasProductInProductInventorySelector, productsFromProductInventoryQuantityPackQuantitySelector } from '../../selectors/ProductInventorySelectors';
import { billingNameKeySelector, productForSkuIdSelector, skuSelector } from '../../selectors/ProductsSelectors';
import classNames from '../../utils/classNames';
import FeatureModalSplitter from '../product-pages/features/FeatureModalSplitter';
import PaidUsersModal from '../product-pages/PaidUsersModal';
import { FreeUsersDisclaimer } from './FreeUsersDisclaimer';
import PriceDisplay from './pricing/PriceDisplay';
const SmallHeader = styled.span.withConfig({
  displayName: "SeatsPicker__SmallHeader",
  componentId: "mozspx-0"
})(["font-size:14px;font-weight:600;"]);
const CompactStepperInput = styled(UIStepperInput).withConfig({
  displayName: "SeatsPicker__CompactStepperInput",
  componentId: "mozspx-1"
})(["display:inline-flex !important;max-width:75px;margin-right:8px;input{font-size:12px;height:34px;}"]);
const StyledInlineTitle = styled(FormattedMessage).withConfig({
  displayName: "SeatsPicker__StyledInlineTitle",
  componentId: "mozspx-2"
})(["font-weight:500;"]);

function SeatsPicker({
  quantityPackSkuId,
  quantityPackQuantity,
  onChange,
  productConfigurations,
  hideFreeUsersDisclaimer,
  useProductLineSpecificTitle,
  className = '',
  compact,
  trackingSource,
  queryParamTerm
}) {
  const {
    quantityUnit: {
      includedWithProductVersion,
      unitType
    },
    productVersionSkuId
  } = useSelector(state => skuSelector(state, {
    skuId: quantityPackSkuId
  }));
  const {
    apiName,
    productLine,
    productLevel
  } = useSelector(state => productForSkuIdSelector(state, {
    skuId: productVersionSkuId
  }));
  const currencyCode = useSelector(activeCurrencySelector);
  const billingNameKey = useSelector(state => billingNameKeySelector(state, {
    skuId: productVersionSkuId
  }));
  const ownedQuantity = useSelector(state => productsFromProductInventoryQuantityPackQuantitySelector(state, {
    productIdentifier: {
      apiName
    },
    unitType
  }));
  const minPurchasableSeats = includedWithProductVersion + ownedQuantity;
  const ownsProduct = useSelector(state => hasProductInProductInventorySelector(state, {
    productAttributeFilters: {
      productLine,
      productLevel
    }
  }));
  const ownedUnitQuantity = ownsProduct ? includedWithProductVersion + ownedQuantity : 0;
  const isMon606Variant = useSelector(isMon606VariantSelector);
  const isMon606InAppRunningForPublicUser = useSelector(isMon606InAppRunningForPublicUserSelector);
  const Mon606HideFreeUsersDisclaimer = isMon606Variant || isMon606InAppRunningForPublicUser || hideFreeUsersDisclaimer;
  useEffect(() => {
    if (quantityPackQuantity < ownedQuantity) {
      onChange(ownedQuantity);
    }
  }, [quantityPackQuantity, ownedQuantity]);
  const additionalUnitPrice = useSelector(state => calculatedPriceForProductConfigurationsInListSelector(state, {
    allProductConfigurations: productConfigurations,
    productConfigurations: [createProductConfig({
      skuId: quantityPackSkuId
    })],
    queryParamTerm
  }).monthly.calculatedPrice);

  const roundValue = ({
    target: {
      value
    }
  }) => {
    const roundedValue = Math.round(value);
    let nextAdditionalUnitQuantity = roundedValue < minPurchasableSeats ? minPurchasableSeats : roundedValue;
    nextAdditionalUnitQuantity = nextAdditionalUnitQuantity - includedWithProductVersion;
    onChange(nextAdditionalUnitQuantity);
  };

  const handleSeatsChange = event => {
    if (event.target.value) {
      getUsageTracker().track('additionalUnitsPicker', {
        trackingSource,
        unitType,
        productLine,
        productLevel,
        ownsProduct,
        skuId: quantityPackSkuId,
        ownedUnitQuantity,
        displayedUnitQuantity: event.target.value
      });
    }

    roundValue(event);
  };

  const titleId = `seats-picker-title-${apiName}`;
  const [paidUsersModalOpen, setPaidUsersModalOpen] = useState(false);
  const [seeAllFeaturesModalOpen, setSeeAllFeaturesModalOpen] = useState(false);
  useEffect(() => {
    if (paidUsersModalOpen) {
      getUsageTracker().track('clickToViewModal', {
        name: 'paid-vs-free-users-modal',
        productLine,
        productLevel,
        location: trackingSource
      });
    }
  }, [paidUsersModalOpen]);
  useEffect(() => {
    if (seeAllFeaturesModalOpen) {
      getUsageTracker().track('clickToViewModal', {
        name: 'compare-feature-modal',
        productLine,
        productLevel,
        location: PAID_VS_FREE_USERS_MODAL
      });
    }
  }, [seeAllFeaturesModalOpen]);

  if (compact) {
    return /*#__PURE__*/_jsxs("div", {
      className: classNames(className, 'text-center'),
      children: [/*#__PURE__*/_jsxs("div", {
        className: "text-center",
        children: [/*#__PURE__*/_jsx(CompactStepperInput, {
          value: quantityPackQuantity + includedWithProductVersion,
          min: minPurchasableSeats,
          onChange: handleSeatsChange,
          "data-selenium-class": "seats-picker-input",
          "data-selenium-id": `${trackingSource}-${apiName}-seats-picker-input`
        }), /*#__PURE__*/_jsx(StyledInlineTitle, {
          message: "pricingPage.seatStepper.title"
        })]
      }), /*#__PURE__*/_jsxs("div", {
        className: "m-top-2",
        children: [/*#__PURE__*/_jsx(Small, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "pricingPage.seatStepper.paidUsersIncluded",
            options: {
              usersIncluded: includedWithProductVersion
            }
          })
        }), "\xA0", /*#__PURE__*/_jsx(Small, {
          children: /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: "pricingPage.seatStepper.pricePerAdditionalUser",
            options: {
              price: /*#__PURE__*/_jsx(PriceDisplay, {
                currencyCode: currencyCode,
                price: additionalUnitPrice
              })
            }
          })
        })]
      }), /*#__PURE__*/_jsx(Small, {
        children: /*#__PURE__*/_jsx(UILink, {
          onClick: () => setPaidUsersModalOpen(true),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "pricingPage.seatStepper.howManyDoINeed"
          })
        })
      }), paidUsersModalOpen && /*#__PURE__*/_jsx(PaidUsersModal, {
        productLevel: productLevel,
        productLine: productLine,
        onCloseClick: () => setPaidUsersModalOpen(false),
        onShowFullFeaturesModal: () => {
          setPaidUsersModalOpen(false);
          setSeeAllFeaturesModalOpen(true);
        }
      }), seeAllFeaturesModalOpen && /*#__PURE__*/_jsx(FeatureModalSplitter, {
        onCloseClick: () => setSeeAllFeaturesModalOpen(false),
        productLine: productLine,
        defaultProductLevel: productLevel
      })]
    });
  } else {
    return /*#__PURE__*/_jsxs("div", {
      className: className,
      children: [useProductLineSpecificTitle ? productLine === SALES ? /*#__PURE__*/_jsx(SmallHeader, {
        id: titleId,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "pricingPage.seatsPicker.hubSpecificTitles.sales"
        })
      }) : /*#__PURE__*/_jsx(SmallHeader, {
        id: titleId,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "pricingPage.seatsPicker.hubSpecificTitles.service"
        })
      }) : /*#__PURE__*/_jsx(H5, {
        id: titleId,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "pricingPage.seatsPicker.title"
        })
      }), /*#__PURE__*/_jsxs(UIFlex, {
        direction: "row",
        justify: "between",
        align: "center",
        children: [/*#__PURE__*/_jsx(Small, {
          style: {
            paddingRight: '15px'
          },
          children: /*#__PURE__*/_jsx(Subtext, {
            children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
              message: `pricingPage.seatsPicker.subtext.${billingNameKey}_jsx`,
              elements: {
                PriceDisplay
              },
              options: {
                currencyCode,
                price: additionalUnitPrice
              }
            })
          })
        }), /*#__PURE__*/_jsx(UIFormControl, {
          "aria-labelledby": titleId,
          children: /*#__PURE__*/_jsx(UIStepperInput, {
            style: {
              width: '100px'
            },
            value: quantityPackQuantity + includedWithProductVersion,
            min: minPurchasableSeats,
            onChange: handleSeatsChange,
            "data-selenium-class": "seats-picker-input",
            "data-selenium-id": `${trackingSource}-${apiName}-seats-picker-input`
          })
        })]
      }), !Mon606HideFreeUsersDisclaimer && /*#__PURE__*/_jsx(FreeUsersDisclaimer, {})]
    });
  }
}

export default SeatsPicker;